import { FORMULA_EVAL_ORDER } from "../../../constants/formulaEvalOrder";
import { sanitizeVariableName } from "../../../utils/formulaScope";
import type { MaterialFormulaFlag } from "../types";
import {
    evaluateFormula,
    getFormulaVariables,
    substituteFormulaExpression,
} from "./evaluateFormula";
import {
    buildEditsByCategory,
    categoryHasFormula,
    findFormulaProduct,
    resolveFormulaExpression,
} from "./materialFormulaUtils";

function buildFormulaInfoForEditKey(
    category: any,
    products: any[],
    materialsEdits: any,
    editKey: string,
): MaterialFormulaFlag {
    const hasFormula = categoryHasFormula(category, products);
    if (!category || !hasFormula) {
        return { hasFormula: false, formulaActive: false };
    }

    const selectedProductId = materialsEdits?.[editKey]?.selectedProductId ?? null;
    const product = selectedProductId
        ? findFormulaProduct(products, category.Id, selectedProductId)
        : null;
    const expression =
        resolveFormulaExpression(category, product) ?? category.DefaultFormula ?? null;

    return {
        hasFormula: true,
        formulaActive: !!selectedProductId && !!resolveFormulaExpression(category, product),
        expression,
    };
}

function applyFormulaEvaluation(
    info: MaterialFormulaFlag,
    expression: string,
    scopeAtCategory: Record<string, any>,
): MaterialFormulaFlag {
    const variables = getFormulaVariables(expression, scopeAtCategory);
    const substitutedExpression = substituteFormulaExpression(
        expression,
        scopeAtCategory,
    );
    const result = evaluateFormula(expression, scopeAtCategory);

    return {
        ...info,
        formulaActive: true,
        expression,
        variables,
        substitutedExpression,
        result,
    };
}

export function calculateMaterialQuantities(
    scope: any,
    formulas: any,
    materialsEdits: any,
    materialsByGroup: any,
) {
    const byCategory = buildEditsByCategory(materialsByGroup);

    const categoryByName = new Map(
        (formulas?.Categories || []).map((c: any) => [c.Name, c]),
    );
    const products: any[] = formulas?.Products || [];
    const workingScope = { ...scope };
    const quantities: any = {};
    const formulaInfo: Record<string, MaterialFormulaFlag> = {};

    for (const rows of Object.values(materialsByGroup || {}) as any[]) {
        for (const row of rows) {
            const category: any = categoryByName.get(row.category);
            const editKeys =
                row.mode === "single"
                    ? [row.id]
                    : row.lines.map((line: any) => line.lineId);

            for (const editKey of editKeys) {
                formulaInfo[editKey] = buildFormulaInfoForEditKey(
                    category,
                    products,
                    materialsEdits,
                    editKey,
                );
            }
        }
    }

    for (const categoryName of FORMULA_EVAL_ORDER) {
        const category: any = categoryByName.get(categoryName);
        if (!category) continue;

        const scopeAtCategory = { ...workingScope };

        let selectedProductId: string | null = null;
        for (const editKey of byCategory[categoryName] || []) {
            selectedProductId = materialsEdits?.[editKey]?.selectedProductId;
            if (selectedProductId) break;
        }
        if (!selectedProductId) continue;

        const product = findFormulaProduct(
            products,
            category.Id,
            selectedProductId,
        );

        const expression = resolveFormulaExpression(category, product);
        if (!expression) {
            quantities[categoryName] = 0;
            continue;
        }

        const qty = evaluateFormula(expression, scopeAtCategory);
        quantities[categoryName] = qty;

        for (const editKey of byCategory[categoryName] || []) {
            const editProductId = materialsEdits?.[editKey]?.selectedProductId;
            if (!editProductId) continue;

            const editProduct = findFormulaProduct(
                products,
                category.Id,
                editProductId,
            );
            const editExpression = resolveFormulaExpression(category, editProduct);
            if (!editExpression) continue;

            formulaInfo[editKey] = applyFormulaEvaluation(
                formulaInfo[editKey] ?? {
                    hasFormula: true,
                    formulaActive: true,
                    expression: editExpression,
                },
                editExpression,
                scopeAtCategory,
            );
        }

        workingScope[sanitizeVariableName(categoryName)] = qty;
    }

    return { quantities, byCategory, formulaInfo };
}
